/**
 * Startup-facing shared DD report (Phase D).
 *
 * Pure row→view mapper for the `get_shared_dd_reports()` RPC rows (safe columns
 * only — never amount/conclusion_notes). Scores + metrics are attached by the
 * hook from the founder's own score_event/profile; this maps the report core +
 * the named investor.
 */

import type { DDMetric } from './dd-metrics';

export interface SharedReportRow {
  id: string;
  /**
   * auth.users.id — get_shared_dd_reports() selects dd_reports.investor_user_id
   * into this column. Named for its id space since 20260808130000; the RPC used
   * to return an id-space-ambiguous name that did NOT match the one declared
   * here, so `investorId` below was undefined for every shared report.
   */
  investor_user_id: string;
  /** Investor name/firm resolved server-side by get_shared_dd_reports() (SECURITY DEFINER). */
  investor_name: string | null;
  investor_firm: string | null;
  startup_id: string;
  startup_name: string | null;
  startup_industry: string | null;
  startup_stage: string | null;
  decision: string | null;
  started_at: string | null;
  concluded_at: string | null;
  documents_reviewed_count: number | null;
  executive_summary: string | null;
  investment_thesis: string | null;
  key_risks: string | null;
  notable_points: { type: 'strength' | 'concern' | 'neutral'; text: string }[] | null;
  narrative_status: string | null;
  shared_at: string | null;
  /** DD Phase B: the pinned "after" score event (null pre-Phase-B / rescore skipped). */
  score_event_end_id: string | null;
}

export interface InvestorIdentity {
  name: string | null;
  firm: string | null;
}

export interface SharedReportView {
  id: string;
  startupId: string;
  investorId: string;
  investorName: string;
  investorFirm?: string;
  decision: string;
  startDate: string | null;
  endDate: string | null;
  documentsReviewed: number;
  executiveSummary?: string;
  investmentThesis?: string;
  keyRisks?: string;
  notablePoints?: { type: 'strength' | 'concern' | 'neutral'; text: string }[];
  sharedAt: string | null;
  // Attached by the hook from score_event / profile:
  scoresAtStart?: { overall: number; confidence: number; risk: number };
  scoresAtEnd?: { overall: number; confidence: number; risk: number };
  metrics?: DDMetric[];
}

export function mapSharedReportRow(row: SharedReportRow, investor?: InvestorIdentity | null): SharedReportView {
  return {
    id: row.id,
    startupId: row.startup_id,
    investorId: row.investor_user_id,
    investorName: investor?.name?.trim() || 'Investor',
    investorFirm: investor?.firm ?? undefined,
    decision: row.decision ?? 'undecided',
    startDate: row.started_at,
    endDate: row.concluded_at,
    documentsReviewed: row.documents_reviewed_count ?? 0,
    executiveSummary: row.executive_summary ?? undefined,
    investmentThesis: row.investment_thesis ?? undefined,
    keyRisks: row.key_risks ?? undefined,
    notablePoints: Array.isArray(row.notable_points) ? row.notable_points : undefined,
    sharedAt: row.shared_at,
  };
}
